import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Button, Col, Row } from 'reactstrap';
import ModalSheet from '../modal-sheet/modal-sheet.component';
import { connect } from 'react-redux';
import {
  setCart,
  setCheckout,
  setOrder,
  setOrderHash,
} from '../../../../redux/store/store.actions';
import {
  setIsCheckoutModalOpen,
  setIsChoosePaymentMethodModalOpen,
  setIsOrderInfoModalOpen,
  setOrderInfoModalData,
  setIsTipModalOpen,
  setIsOrderNotificationsModalOpen,
  setOrderNotificationsModalData,
} from '../../../../redux/modal/modal.actions';
import PropTypes from 'prop-types';
import {
  calculateCartTotal,
  getActiveOrderConfig,
  getTipSettings,
  getLocalizedOrderHint,
  replaceColorInLottie,
  hasEnabledNotificationMethods,
} from '../../../../utility/util/util.service';
import TagManager from 'react-gtm-module/dist/TagManager';
import {
  ORDER_STATUS_PENDING,
  CHECKOUT_METHOD_PAY_AND_GO,
} from '../../../../utility/constants';
import CustomTipModal from '../custom-tip/custom-tip.modal';
import xss from 'xss';
import heart1 from '../../../../assets/lottie/heart-1.json';
import Lottie from 'lottie-react';

const TipModal = ({
  isOpen,
  cart,
  store,
  order,
  checkout,
  kiosk,
  table,
  me,

  setCheckout,
  setIsCheckoutModalOpen,
  setIsChoosePaymentMethodModalOpen,
  setCart,
  setOrder,
  setOrderHash,
  setOrderInfoModalData,
  setIsOrderInfoModalOpen,
  setIsTipModalOpen,
  setIsOrderNotificationsModalOpen,
  setOrderNotificationsModalData,
}) => {
  const intl = useIntl();
  const total = calculateCartTotal(checkout?.orderType, cart);
  const roundUpTip = (total && Math.ceil(total) - total) || 0;
  const [showCustomTipModal, setShowCustomTipModal] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [showFadeOutAnimation, setShowFadeOutAnimation] = useState(false);

  // Get tip settings from the active order config for the current order type
  const activeOrderConfig = getActiveOrderConfig(
    store,
    checkout?.orderType,
    table,
    kiosk,
  );
  const tipSettings = getTipSettings(activeOrderConfig);
  const tipCategories = tipSettings.tipCategories;
  const tipHint = tipSettings.tipHint;
  const localizedTipHint = getLocalizedOrderHint(tipHint, intl.locale);

  const thanksAnimation = useMemo(
    () => (
      <Lottie
        style={{ width: '100%' }}
        className="mr-1"
        // rendererSettings={{ viewBoxSize: '200 200 600 600', viewBoxOnly: true }}
        animationData={replaceColorInLottie(
          heart1,
          store?.primaryColor || '#22442c',
        )}
        loop={false}
      />
    ),
    [store?.primaryColor],
  );

  // if the payment succeeds while this modal is open, jump to the orderInfoModal
  useEffect(() => {
    if (isOpen && order && order.status !== ORDER_STATUS_PENDING) {
      setCheckout(null);
      setCart(null);
      setOrder(null);
      setOrderHash(null);
      setOrderInfoModalData({ order, isInitial: true });
      setIsOrderInfoModalOpen(true);
    }
  }, [order]);

  const handleSubmit = (tipFactor, tipAbsolute = null) => {
    // Set the tip in checkout
    setCheckout({ tip: tipAbsolute || total * tipFactor });

    if (!tipFactor && !tipAbsolute) {
      // No tip - proceed directly
      openNextModal();
    } else {
      // Show animation then proceed
      setShowFadeOutAnimation(true);
      setTimeout(() => {
        setShowFadeOutAnimation(false);
        setShowAnimation(true);
      }, 500);
      setTimeout(() => {
        TagManager.dataLayer({
          dataLayer: {
            event: 'AddOrderTip',
            orderId: order?.id,
            tip: tipAbsolute || total * tipFactor,
          },
        });
        setShowAnimation(false);
        setShowFadeOutAnimation(false);
        openNextModal();
      }, 3000); // delay animation ending
    }
  };

  const openNextModal = () => {
    // V2: Check if notification methods should be shown before checkout
    const orderConfigKey = {
      SelfServiceOrder: 'selfServiceOrderConfig',
      PickupOrder: 'pickupOrderConfig',
      InhouseOrder: 'inhouseOrderConfig',
      DeliveryOrder: 'deliveryOrderConfig',
      CateringOrder: 'cateringOrderConfig',
    }[checkout?.orderType];
    const currentOrderConfig = typeof store?.[orderConfigKey] === 'object' ? store[orderConfigKey] : null;
    const hasNotificationMethods = hasEnabledNotificationMethods(currentOrderConfig);
    // pay_and_go is the inhouse table checkout that follows an order_only add,
    // where the notification method was already collected — don't ask again.
    const isPayAndGo = checkout?.checkoutMethod === CHECKOUT_METHOD_PAY_AND_GO;

    if (hasNotificationMethods && !kiosk && !isPayAndGo) {
      setIsTipModalOpen(false);
      setOrderNotificationsModalData({ proceedToCheckout: true, checkout });
      setIsOrderNotificationsModalOpen(true);
      return;
    }

    const shouldShowPaymentMethodChooser =
      kiosk &&
      store?.paymentProcessor?.enabledPaymentMethods?.includes('twint');

    if (shouldShowPaymentMethodChooser) {
      setIsChoosePaymentMethodModalOpen(true);
    } else {
      setIsCheckoutModalOpen(true);
    }
  };

  const customXss = new xss.FilterXSS({
    whiteList: {
      p: [],
      b: [],
      br: [],
      em: [],
      u: [],
      i: [],
      strong: [],
      a: ['href', 'target'],
    },
  });

  const SanitizedHTML = ({ htmlContent }) => {
    const sanitizedHtml = customXss.process(htmlContent);

    return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
  };

  return (
    <ModalSheet
      isOpen={isOpen}
      closeModal={() => handleSubmit(0)}
      // title={intl.formatMessage({ id: 'tipTitle' })}
      enableDrag={true}
      disableEnterToSubmit={true}
    >
      <div className="px-1">
        {!showAnimation && (
          <div
            className={`${
              showFadeOutAnimation
                ? 'animate__fadeOut animate__animated animate-duration-05'
                : ''
            }`}
          >
            <span className="font-medium-2 black d-block px-50 text-center">
              {localizedTipHint ? (
                <SanitizedHTML
                  wrapperClassName="d-block text-left font-medium-1 black"
                  htmlContent={localizedTipHint
                    ?.replace(
                      '$customerName$',
                      me?.requester?.customerProfile?.name || me?.name || '',
                    )
                    .replace('$storeName$', store?.name)}
                />
              ) : (
                <FormattedMessage
                  id="tipText"
                  values={{ storeName: store?.name }}
                />
              )}
            </span>
            {tipCategories?.length > 0 && (
              <Row
                className="my-2 px-50 d-flex align-items-center"
                style={{ height: '75px' }}
              >
                <Col xs="4" className="px-50" style={{ height: '60px' }}>
                  <Button
                    data-testid="firstButtonTip"
                    color="primary"
                    className="w-100 px-50 py-75 h-100"
                    onClick={() => handleSubmit(tipCategories[0] / 100)}
                  >
                    <span className="d-block font-medium-1 mb-25 text-center">
                      {tipCategories[0]}%
                    </span>
                    <span className="d-block font-small-2 text-center">
                      (
                      <FormattedNumber
                        value={total * (tipCategories[0] / 100)}
                        format="priceCurrency"
                      />
                      )
                    </span>
                  </Button>
                </Col>
                <Col xs="4" className="px-50" style={{ height: '70px' }}>
                  <Button
                    data-testid="secondButtonTip"
                    color="primary"
                    className="w-100 px-50 py-75 h-100"
                    onClick={() => handleSubmit(tipCategories[1] / 100)}
                  >
                    <span className="d-block font-medium-3 mb-25 text-center">
                      {tipCategories[1]}%
                    </span>
                    <span className="d-block font-small-2 text-center">
                      (
                      <FormattedNumber
                        value={total * (tipCategories[1] / 100)}
                        format="priceCurrency"
                      />
                      )
                    </span>
                  </Button>
                </Col>
                <Col xs="4" className="px-50" style={{ height: '60px' }}>
                  <Button
                    data-testid="thirdButtonTip"
                    color="primary"
                    className="w-100 px-50 py-75 h-100"
                    onClick={() => handleSubmit(tipCategories[2] / 100)}
                  >
                    <span className="d-block font-medium-1 mb-25 text-center">
                      {tipCategories[2]}%
                    </span>
                    <span className="d-block font-small-2 text-center">
                      (
                      <FormattedNumber
                        value={total * (tipCategories[2] / 100)}
                        format="priceCurrency"
                      />
                      )
                    </span>
                  </Button>
                </Col>
              </Row>
            )}

            <Row style={{ minHeight: '40px' }} className="my-2 px-50">
              {tipSettings.allowTipCustomAmount && (
                <Col
                  xs={
                    tipSettings.allowTipRoundUp &&
                    tipSettings.allowTipCustomAmount &&
                    roundUpTip > 0
                      ? '6'
                      : '12'
                  }
                  className="px-50 d-flex"
                  style={{ minHeight: '100%' }}
                >
                  <Button
                    data-testid="customButtonTip"
                    color="transparent"
                    className="w-100 px-50 py-75 border-primary dashed"
                    onClick={() => setShowCustomTipModal(true)}
                  >
                    <span className="d-block text-primary font-medium-1 text-center">
                      <FormattedMessage id="customTipLabel" />
                    </span>
                  </Button>
                </Col>
              )}
              {tipSettings.allowTipRoundUp && roundUpTip > 0 && (
                <Col
                  xs={
                    tipSettings.allowTipRoundUp &&
                    tipSettings.allowTipCustomAmount
                      ? '6'
                      : '12'
                  }
                  className="px-50 d-flex"
                  style={{ minHeight: '100%' }}
                >
                  <Button
                    data-testid="roundUpButtonTip"
                    color="transparent"
                    className="w-100 px-50 py-75 border-primary dashed"
                    onClick={() => handleSubmit(null, roundUpTip)}
                  >
                    <span className="d-block text-primary font-medium-1 text-center">
                      <FormattedMessage id="tipRoundUpLabel" /> (
                      <FormattedNumber
                        value={roundUpTip}
                        format="priceCurrency"
                      />
                      )
                    </span>
                  </Button>
                </Col>
              )}
            </Row>

            <span
              className="d-block px-50 my-50 primary font-small-3 text-center"
              onClick={() => handleSubmit(0)}
            >
              <FormattedMessage id="tipRefuseLabel" />
            </span>
            <CustomTipModal
              isOpen={showCustomTipModal}
              closeModal={() => setShowCustomTipModal(false)}
              handleSaveTip={(tip) => handleSubmit(null, tip)}
            />
          </div>
        )}
        {showAnimation && (
          <>
            {thanksAnimation}
            <span className="d-block px-50 my-50 primary font-medium-3 text-center">
              {me?.requester?.customerProfile.name ? (
                <FormattedMessage
                  id="tipThanksCustomerLabel"
                  values={{
                    customerName: me.requester.customerProfile.name,
                  }}
                />
              ) : (
                <FormattedMessage
                  id="tipThanksDefaultLabel"
                  values={{ storeName: store?.name }}
                />
              )}
            </span>
          </>
        )}
      </div>
    </ModalSheet>
  );
};

TipModal.propTypes = {
  isOpen: PropTypes.bool,
  cart: PropTypes.array,
  intl: PropTypes.object,
  store: PropTypes.object,
  setIsCheckoutModalOpen: PropTypes.func,
  setCheckout: PropTypes.func,
};

const mapStateToProps = (state) => ({
  isOpen: state.modals.isTipModalOpen,
  cart: state.store.cart,
  store: state.store.store,
  order: state.store.order,
  table: state.store.table,
  checkout: state.store.checkout,
  kiosk: state.store.kiosk,
  me: state.me.me,
});

export default connect(mapStateToProps, {
  setCheckout,
  setIsCheckoutModalOpen,
  setIsChoosePaymentMethodModalOpen,
  setCart,
  setOrder,
  setOrderHash,
  setOrderInfoModalData,
  setIsOrderInfoModalOpen,
  setIsTipModalOpen,
  setIsOrderNotificationsModalOpen,
  setOrderNotificationsModalData,
})(TipModal);
